// chunkRecovery.ts — EV-845
// Auto-recover from "Importing a module script failed" / "Failed to fetch dynamically
// imported module" — the crash returning visitors hit when a STALE cached shell (old
// service worker / cached index.html) references a lazy JS chunk hash that no longer
// matches the deployed assets. Tracy / Gia / Patti all white-screened on the shared
// birthday link this way. Recovery = nuke caches + unregister the SW + hard reload so
// the device pulls a fresh, self-consistent shell + chunks.

const CHUNK_ERR_RE = /importing a module script failed|failed to (fetch|load) dynamically imported module|error loading dynamically imported module|module script failed|unable to preload|dynamically imported module|_result\.default|ChunkLoadError|Loading chunk \d+ failed/i;

export function isChunkError(msg?: string | null): boolean {
  return !!msg && CHUNK_ERR_RE.test(String(msg));
}

let recovering = false;

// 🔴 v70.046 — RETRY WITH BACKOFF, NOT ONCE.
//
// Patrick, 2026-07-31, after a production deploy while he was using the site:
//   "prod is not healthy i hard refreshed same shit
//    TypeError: Failed to fetch dynamically imported module ...
//    Failed to load module script: ... the server responded with a MIME type of
//    \"text/html\""
//
// The recovery below already existed and its regex DID match his error. What defeated it
// was the guard: it allowed ONE reload per 20 second window and then deliberately gave up
// so a genuine bug could surface. That reasoning is right for a code bug and WRONG for the
// actual cause here.
//
// The real cause is Cloudflare Pages edge propagation. Right after a deploy, an edge node
// may not yet hold the new hashed chunk, so it serves the SPA fallback — index.html, with
// content-type text/html — and the module loader rejects it. Propagation observed at
// 10-30 SECONDS during this session (a verification loop needed three attempts spaced 10s).
// So one retry inside a 20s window lands squarely in the dead zone, gives up, and shows the
// ErrorBoundary. Exactly what he saw.
//
// Now: up to MAX_ATTEMPTS reloads with a short escalating delay, so a device that hits a
// deploy mid-flight rides it out instead of dying. The attempt counter still bounds it, so a
// genuine broken build surfaces rather than reload-looping forever.
const MAX_ATTEMPTS = 4;
/** Waits before each reload (ms). Total ~26s, comfortably past observed propagation. */
const BACKOFF_MS = [1200, 4000, 9000, 12000];
const ATTEMPT_KEY = 'hl_chunk_recover_n';
const WINDOW_KEY = 'hl_chunk_recover_ts';
/** Attempts older than this belong to a previous incident — start counting again. */
const WINDOW_MS = 120000;

export function triggerChunkRecovery(): void {
  if (recovering) return;

  let attempt = 0;
  try {
    const last = Number(sessionStorage.getItem(WINDOW_KEY) || '0');
    const prev = Number(sessionStorage.getItem(ATTEMPT_KEY) || '0');
    // A fresh incident if the last attempt was long ago.
    attempt = (last && Date.now() - last < WINDOW_MS) ? prev : 0;
    if (attempt >= MAX_ATTEMPTS) return;   // genuinely broken — let the ErrorBoundary show
    sessionStorage.setItem(WINDOW_KEY, String(Date.now()));
    sessionStorage.setItem(ATTEMPT_KEY, String(attempt + 1));
  } catch { /* sessionStorage unavailable — proceed with a single attempt */ }

  recovering = true;
  (async () => {
    try {
      if ('caches' in window) {
        const ks = await caches.keys();
        await Promise.all(ks.map((k) => caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        const rs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(rs.map((r) => r.unregister()));
      }
    } catch { /* best effort */ }
    // Wait before reloading so the edge has a moment to catch up. Reloading instantly is
    // what made the single-shot version useless — it raced the propagation and lost.
    const wait = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
    await new Promise((r) => setTimeout(r, wait));
    // Hard reload — fresh index.html (no-cache) + fresh chunks, no SW in the way.
    window.location.reload();
  })();
}

/** Call once the app has rendered successfully — the incident is over. */
export function clearChunkRecoveryState(): void {
  try {
    sessionStorage.removeItem(ATTEMPT_KEY);
    sessionStorage.removeItem(WINDOW_KEY);
  } catch { /* ignore */ }
}

export function installChunkErrorRecovery(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (e: any) => {
    const msg = e?.message || e?.error?.message || '';
    if (isChunkError(msg)) triggerChunkRecovery();
  }, true);
  window.addEventListener('unhandledrejection', (e: any) => {
    const msg = e?.reason?.message || String(e?.reason || '');
    if (isChunkError(msg)) triggerChunkRecovery();
  });
  // Vite emits this when a preloaded module fails to load.
  window.addEventListener('vite:preloadError', () => triggerChunkRecovery());
}
